import { makeAssistantToolUI } from '@assistant-ui/react'
import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type { ToolActionHandlers } from '@/components/agent/tools/_shared/chat-tool-actions'
import { ToolCardActions } from '@/components/agent/tools/_shared/ToolCardActions'
import { ShowEntitiesAtLocationView } from '@/components/agent/tools/ShowEntitiesAtLocationView'
import {
  parseResult,
  type ShowEntitiesAtLocationArgs,
  type ShowEntitiesAtLocationResult,
} from './show-entities-at-location'
import { useToolCardActions } from './useToolCardActions'

type Status = { type: 'running' | 'complete' | 'incomplete' | string }

interface ShowEntitiesAtLocationRenderProps {
  args: ShowEntitiesAtLocationArgs
  result?: ShowEntitiesAtLocationResult | string
  status: Status
}

/**
 * Connected `show_entities_at_location` tool card: parses the backend payload,
 * builds the "show on map" handler (stashes the pending map/timeline target in
 * sessionStorage, then navigates to the case graph), resolves the action
 * footer from the chat context, and hands pure data to the view.
 */
function ShowEntitiesAtLocationRender({ args, result, status }: ShowEntitiesAtLocationRenderProps) {
  const navigate = useNavigate()
  const { caseId } = useParams<{ caseId: string }>()

  const running = status.type === 'running'
  const parsed = useMemo(() => parseResult(result), [result])
  const errored = parsed?.status === 'error' || status.type === 'incomplete'

  const entityIds = useMemo(() => (parsed?.entities ?? []).map(e => e.element_id), [parsed])

  const handlers = useMemo<ToolActionHandlers>(() => {
    if (parsed?.status !== 'ok' || !parsed.location || !caseId) return {}
    const location = parsed.location
    const timeRange = parsed.time_range
    const entityElementIds = (parsed.entities ?? []).map(e => e.element_id)

    const showOnMap = () => {
      if (timeRange) {
        sessionStorage.setItem(
          'spectre:pendingTimeline',
          JSON.stringify({
            globalTimeRange: timeRange,
            filterWindow: timeRange,
            playheadTime: timeRange.start,
            openTimeline: true,
          })
        )
      }

      sessionStorage.setItem(
        'spectre:pendingMapTarget',
        JSON.stringify({
          lat: location.latitude,
          lon: location.longitude,
          zoom: 14,
          entityIds: entityElementIds,
        })
      )

      navigate(`/cases/${caseId}/graph`)
    }

    return { showOnGraph: showOnMap }
  }, [parsed, caseId, navigate])

  const actionsModel = useToolCardActions(entityIds, handlers)
  const actions = actionsModel ? <ToolCardActions model={actionsModel} handlers={handlers} /> : null

  return (
    <ShowEntitiesAtLocationView
      args={args}
      parsed={parsed}
      running={running}
      errored={errored}
      actions={actions}
    />
  )
}

export const ShowEntitiesAtLocationToolUI = makeAssistantToolUI<
  ShowEntitiesAtLocationArgs,
  ShowEntitiesAtLocationResult | string
>({
  toolName: 'show_entities_at_location',
  render: ShowEntitiesAtLocationRender,
})
